/**
 * UGC submission video preview.
 *
 * Reads every `video` URL parameter and renders a native player for each one that
 * points at a Hulk Form Builder upload for this store. Parameter values are never
 * written as HTML: elements are created one by one and URLs are set as properties.
 */

const ALLOWED_HOST = 'form-builder-by-hulkapps.s3.amazonaws.com';
const ALLOWED_PATH_PREFIX = '/uploads/buff-browz.myshopify.com/';
const ALLOWED_EXTENSION = /\.(mp4|mov|m4v|webm)$/i;

const MESSAGES = {
  empty: 'No video specified.',
  invalid: "This link isn't a valid submission video",
  unplayable:
    "This video can't play in your browser. It may be an iPhone HEVC file. Try Safari, or download it below.",
  download: 'Download',
};

/**
 * Returns the parsed URL when it is an allowed submission video, otherwise null.
 * @param {string} value - Raw `video` parameter value.
 * @returns {URL | null}
 */
function parseSubmissionUrl(value) {
  /** @type {URL} */
  let url;

  try {
    url = new URL(value);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:') return null;
  if (url.hostname !== ALLOWED_HOST) return null;
  if (url.port !== '' || url.username !== '' || url.password !== '') return null;
  if (!url.pathname.startsWith(ALLOWED_PATH_PREFIX)) return null;
  if (!ALLOWED_EXTENSION.test(url.pathname)) return null;

  return url;
}

/**
 * @param {URL} url
 * @returns {string} The decoded file name from the URL path.
 */
function getFileName(url) {
  const segment = url.pathname.split('/').pop() ?? '';

  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * @param {string} text
 * @returns {HTMLParagraphElement}
 */
function createMessage(text) {
  const message = document.createElement('p');
  message.className = 'ugc-preview__message';
  message.textContent = text;
  return message;
}

/**
 * @param {URL} url
 * @returns {HTMLDivElement}
 */
function createPlayer(url) {
  const item = document.createElement('div');
  item.className = 'ugc-preview__item';

  const video = document.createElement('video');
  video.className = 'ugc-preview__video';
  video.controls = true;
  video.playsInline = true;
  video.preload = 'metadata';

  const showUnplayable = () => {
    if (!video.isConnected) return;
    video.pause();
    video.replaceWith(createMessage(MESSAGES.unplayable));
  };

  video.addEventListener('error', showUnplayable);
  // Chrome can load an HEVC file without an error and play audio over a blank frame
  video.addEventListener('loadedmetadata', () => {
    if (video.videoWidth === 0) {
      showUnplayable();
      return;
    }

    // Size the player to the clip so portrait phone videos are not shrunk into a landscape box
    video.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
  });
  video.src = url.href;

  const meta = document.createElement('div');
  meta.className = 'ugc-preview__meta';

  const name = document.createElement('p');
  name.className = 'ugc-preview__name';
  name.textContent = getFileName(url);

  const download = document.createElement('a');
  download.className = 'ugc-preview__download';
  download.href = url.href;
  download.download = '';
  download.rel = 'noopener noreferrer';
  download.textContent = MESSAGES.download;

  meta.append(name, download);
  item.append(video, meta);

  return item;
}

/**
 * @param {Element} container
 */
function render(container) {
  const values = new URLSearchParams(window.location.search).getAll('video').filter((value) => value.trim() !== '');

  if (values.length === 0) {
    container.replaceChildren(createMessage(MESSAGES.empty));
    return;
  }

  container.replaceChildren(
    ...values.map((value) => {
      const url = parseSubmissionUrl(value.trim());
      return url ? createPlayer(url) : createMessage(MESSAGES.invalid);
    })
  );
}

for (const container of document.querySelectorAll('[data-ugc-preview]')) {
  render(container);
}
